import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, CatalogDashboardDto as CatalogDashboardDto4Analytics } from "@interfaces/HttpClient/AnalyticsApiModels";
import { AdditionalInfoCountsDto, CadBimCountsDto, Client as CatalogApi, CatalogBranchDto, CatalogCheckDto, CatalogItemMatchingCountsDto, CatalogItemsCountsDto, CatalogItemsWithAdditionalFilePercentagesDto, CatalogItemsWithAdditionalInfoPercentagesDto, DocumentCountsDto, FileLinkCountsDto, ImageVideoCountsDto, LinkCountsDto, LivePreviewComparisonCountsDto, LivePreviewPlusInformationComparisonCountsDto, PlusInformationCountsDto, PreviewCatalogDto, PreviewInfoDto, UnresolvedLinkCountsDto, ViewerLinkCountsDto } from "@interfaces/HttpClient/CatalogApiModels";
import { CatalogDashboardDto as CatalogDashboardDto4Nlp, Client as NaturalLanguageProcessingApi } from "@interfaces/HttpClient/NaturalLanguageProcessingApiModels";
import { firstValueFrom } from 'rxjs';
import { AdministrationService } from './administration.service';

@Injectable()
export class CatalogDashboardPreviewCatalogService {

	constructor(
		private analyticsApi: AnalyticsApi,
		private catalogApi: CatalogApi,
		private nlpApi: NaturalLanguageProcessingApi,
		private requestService: RequestService,
		private administrationService: AdministrationService
	) { }

	async getCatalogKeyByPublicCatalogId(catalogId: number): Promise<string> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogKeyByPublicCatalogId(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined },
			]);
		}
	}

	async getPreviewInfoDate(catalogId: number): Promise<PreviewInfoDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetPreviewInfo(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined },
			]);
		}
	}

	async getIntegrationStatus(catalogId: number): Promise<CatalogDashboardDto4Analytics> {
		try {
			return firstValueFrom(this.analyticsApi.catalogDashboard_GetDashboardInfo(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogCheck(catalogId: number): Promise<CatalogCheckDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogCheck(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogItemsCounts(catalogId: number): Promise<CatalogItemsCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogItemsCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getPlusInformationCounts(catalogId: number): Promise<PlusInformationCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetPlusInformationCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogItemMatchingCounts(catalogId: number): Promise<CatalogItemMatchingCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogItemMatchingCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getKeywordListsByCatalogId(catalogId: number): Promise<CatalogDashboardDto4Nlp> {
		try {
			return firstValueFrom(this.nlpApi.catalogDashboard_GetKeywordListsByCatalogId(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogComparisonByCatalogId(catalogId: number): Promise<LivePreviewComparisonCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetLivePreviewComparisonCountsByCatalogId(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogComparisonPlusInfosByCatalogId(catalogId: number): Promise<LivePreviewPlusInformationComparisonCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetLivePreviewPlusInformationComparisonCountsByCatalogId(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getPreviewCatalogByPublicCatalogId(catalogId: number): Promise<PreviewCatalogDto> {
		try {
			return firstValueFrom(this.catalogApi.nlpApi_GetPreviewCatalogByPublicCatalogId(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogBranchesByCatalogId(catalogId: number): Promise<CatalogBranchDto[]> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetCatalogBranchesByCatalogId(catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	//  _     _             _____       _        _                         ___  _   _             _                          _         ______     _        _ _
	// | |   (_)           /  __ \     | |      | |                       / _ \| | | |           | |                        | |        |  _  \   | |      (_) |
	// | |    ___   _____  | /  \/ __ _| |_ __ _| | ___   __ _   ______  / /_\ \ |_| |_ __ _  ___| |__  _ __ ___   ___ _ __ | |_ ______| | | |___| |_ __ _ _| |___
	// | |   | \ \ / / _ \ | |    / _` | __/ _` | |/ _ \ / _` | |______| |  _  | __| __/ _` |/ __| '_ \| '_ ` _ \ / _ \ '_ \| __|______| | | / _ \ __/ _` | | / __|
	// | |___| |\ V /  __/ | \__/\ (_| | || (_| | | (_) | (_| |          | | | | |_| || (_| | (__| | | | | | | | |  __/ | | | |_       | |/ /  __/ || (_| | | \__ \
	// \_____/_| \_/ \___|  \____/\__,_|\__\__,_|_|\___/ \__, |          \_| |_/\__|\__\__,_|\___|_| |_|_| |_| |_|\___|_| |_|\__|      |___/ \___|\__\__,_|_|_|___/
	//                                                    __/ |
	//                                                   |___/

	async getCatalogItemsWithAdditionalInfoPercentages(catalogId: number): Promise<CatalogItemsWithAdditionalInfoPercentagesDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogItemsWithAdditionalInfoPercentages(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogItemsWithAdditionalFilePercentages(catalogId: number): Promise<CatalogItemsWithAdditionalFilePercentagesDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCatalogItemsWithAdditionalFilePercentages(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getAdditionalInfoCounts(catalogId: number): Promise<AdditionalInfoCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetAdditionalInfoCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getImageVideoCounts(catalogId: number): Promise<ImageVideoCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetImageVideoCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getDocumentCounts(catalogId: number): Promise<DocumentCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetDocumentCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCadBimCounts(catalogId: number): Promise<CadBimCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetCadBimCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getLinkCounts(catalogId: number): Promise<LinkCountsDto> {
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetLinkCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getFileLinkCounts(id?: number): Promise<FileLinkCountsDto> {
		const catalogId = id ?? this.administrationService.getId();
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetFileLinkCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);

		}
	}

	async getViewerLinkCounts(id?: number): Promise<ViewerLinkCountsDto> {
		const catalogId = id ?? this.administrationService.getId();
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetViewerLinkCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);

		}
	}

	async getUnresolvedLinkCounts(id?: number): Promise<UnresolvedLinkCountsDto> {
		const catalogId = id ?? this.administrationService.getId();
		try {
			return firstValueFrom(this.catalogApi.catalogDashboardPreviewCatalog_GetUnresolvedLinkCounts(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);

		}
	}
}
